var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { P, Segments, SharedColors, SharedGradients } from "apparatus/library/common";
import { Ray } from "apparatus/library/physics/optical/common";
import { Group, Path } from "paper";
import { staticImplements } from "types";
const MIN_FOCAL_LENGTH = 60;
const MAX_FOCAL_LENGTH = 500;
let Lens = class Lens {
    constructor() {
        this.drivers = [
            {
                type: "rectangle",
                minX: MIN_FOCAL_LENGTH, maxX: MAX_FOCAL_LENGTH,
                minY: 0, maxY: 0,
                fromAppearance: a => P(a.focalLength, 0),
                toAppearance: (f, p) => f({ focalLength: p.x }),
                scale: 1.0,
                visible: a => isConvex(a.type) && a.showRay
            },
            {
                type: "rectangle",
                maxX: -MIN_FOCAL_LENGTH, minX: -MAX_FOCAL_LENGTH,
                minY: 0, maxY: 0,
                fromAppearance: a => P(-a.focalLength, 0),
                toAppearance: (f, p) => f({ focalLength: -p.x }),
                scale: 1.0,
                visible: a => !isConvex(a.type) && a.showRay
            },
        ];
    }
    render(appearance) {
        const { focalLength, type, rays, dottedLine } = appearance;
        const lens = createLens(appearance.type).mirrorY().close()
            .withGradientFill("right-down", SharedGradients.glass)
            .withStroke("thin", SharedColors.glassEdge);
        const shooter = new RayShooter(focalLength, lens, appearance);
        if (appearance.showRay) {
            if (type === "biconvex") {
                if (rays === "five") {
                    [40, 20, 0, -20, -40].map(y => shooter.shootConvex(-100, y));
                }
                else if (rays === "two") {
                    [40, -40].map(y => shooter.shootConvex(-100, y));
                }
            }
            else if (type === "biconcave") {
                if (rays === "five") {
                    [-36, -18, 0, 18, 36].map(y => shooter.shootConcave(-100, y, 200, dottedLine));
                }
                else if (rays === "two") {
                    [-36, 36].map(y => shooter.shootConcave(-100, y, 200, dottedLine));
                }
            }
        }
        const graphic = new Group([shooter.rays, lens]);
        return {
            graphic,
            hitShape: lens.bounds.expand(10).toShape(),
            pivot: P(0, 0),
        };
    }
};
Lens.properties = {
    label: "Lens",
    flippable: true,
    defaultAppearance: Object.assign(Object.assign(Object.assign({ type: "biconcave", focalLength: 200 }, Ray.defaultAppearanceRayOn), Ray.defaultAppearanceArrow), { rays: "five", dottedLine: false }),
    appearanceSpecs: [
        {
            spec: "radio", key: "type", label: "Type",
            options: [
                { value: "biconvex", label: "Biconvex" },
                { value: "biconcave", label: "Biconcave" },
            ],
        },
        {
            spec: "slider", key: "focalLength", label: "Focal length",
            min: MIN_FOCAL_LENGTH, max: MAX_FOCAL_LENGTH, unit: "mm"
        },
        ...Ray.specs,
        {
            spec: "radio", key: "rays", label: "Ray type",
            options: [
                { value: "two", label: "2 rays" },
                { value: "five", label: "5 rays" },
            ],
            visible: a => a.showRay,
        },
        {
            spec: "checkbox-group", label: "Show",
            checkboxes: [
                { key: "dottedLine", label: "Virtual rays", visible: a => a.showRay && !isConvex(a.type) },
                { key: "showRayArrow", label: "Arrows", visible: a => a.showRay },
            ]
        }
    ],
};
Lens = __decorate([
    staticImplements()
], Lens);
export { Lens };
class RayShooter {
    constructor(focalLength, path, appearance) {
        this.focalLength = focalLength;
        this.path = path;
        this.appearance = appearance;
        this.rays = new Group();
    }
    shootConvex(x, y) {
        const path1 = new Path([[x, y], [0, y]]);
        const ints1 = this.path.getIntersections(path1);
        const path2 = new Path([[0, y], [this.focalLength, 0]]);
        const ints2 = this.path.getIntersections(path2);
        if (ints1.length == 1 && ints2.length == 1) {
            const ray = new Path([[x, y], ints1[0].point, ints2[0].point, [this.focalLength, 0]]);
            Ray.applyStyle(ray, this.appearance);
            this.rays.addChild(ray);
            this.maybeAddArrows(ray, [0.5, null, 0.3]);
        }
        path1.remove();
        path2.remove();
    }
    shootConcave(x, y, distance, dottedLine) {
        const path1 = new Path([[x, y], [0, y]]);
        const ints1 = this.path.getIntersections(path1);
        const path2 = new Path([[0, y], [this.focalLength, y * 2]]);
        const ints2 = this.path.getIntersections(path2);
        if (ints1.length == 1 && ints2.length == 1) {
            const ray = new Path([[x, y], ints1[0].point, ints2[0].point]);
            const emergent = P(this.focalLength, y);
            if (dottedLine) {
                const ray2 = new Path([[-this.focalLength, 0], ints2[0].point]);
                ray2.dashArray = [this.appearance.rayThickness, this.appearance.rayThickness * 2];
                Ray.applyStyle(ray2, this.appearance);
                this.rays.addChild(ray2);
            }
            emergent.length = distance;
            ray.lineBy(emergent);
            Ray.applyStyle(ray, this.appearance);
            this.rays.addChild(ray);
            this.maybeAddArrows(ray, [0.5, null, 1.0]);
        }
        path1.remove();
        path2.remove();
    }
    maybeAddArrows(ray, positions) {
        Ray.maybeAddArrows(this.appearance, ray, positions, this.rays);
    }
}
function createLens(type) {
    switch (type) {
        case "biconvex":
            return new Path(Segments([[-5, -50], [-15, 25]], [[5, -50], , [15, 25]]));
        case "biconcave":
            return new Path(Segments([[-15, -50], [12, 25]], [[15, -50], , [-12, 25]]));
    }
}
function isConvex(type) {
    switch (type) {
        case "biconcave":
            return false;
        case "biconvex":
            return true;
    }
}
